import { isDefined } from '@app/utils/is';
import { useMutation } from '@tanstack/react-query';
import { Lookbook } from '@typings';
import { useTranslation } from 'react-i18next';

import { addToast } from '../../../components/various/Toasts';
import { createSelectionFromWishlist } from '../../lookbookRepository';

interface Payload {
  data: Lookbook.CreateSelectionFromWishlistFormData;
}

export const useCreateNewSelectionFromWishlist = ({ onSuccess }: { onSuccess?: VoidFunction }) => {
  const { t } = useTranslation(['lookbook']);

  return useMutation({
    mutationFn: async ({ data }: Payload) => {
      const dataDto: Lookbook.CreateSelectionFromWishlistDTO = {
        buyers: data.buyers?.map(buyer => buyer.buyer) ?? [],
        deliveryWindows: data.deliveryWindows,
        name: data.selectionName,
        products: data.products,
      };

      return createSelectionFromWishlist(dataDto);
    },
    onError: (error: { status?: number; message: string }) => {
      if (error.status === 406) {
        addToast(error.message, { description: t('lookbook:selection_creation_error_406'), type: 'error' });

        return;
      }
      addToast(error.message, { type: 'error' });
    },
    onSuccess: (data, context) => {
      data.forEach(item => {
        const hasError = item.error.length > 0;
        const hasSuccess = item.success.length > 0;
        const isError = hasError && !hasSuccess;
        const isPartial = hasSuccess && hasError;

        const errorCount = new Set(item.error).size;
        const buyerName = context.data.buyers?.find(b => b.buyer === item.buyer.toString())?.name;
        onSuccess?.();

        if (isError) {
          if (isDefined(buyerName)) {
            addToast(t('lookbook:selection_creation_error_title'), {
              description: t('lookbook:selection_creation_error_description', { buyer: buyerName }),
              type: 'error',
            });

            return;
          }
          addToast(t('lookbook:selection_creation_error_title'), {
            description: t('lookbook:selection_creation_error_buyer_description'),
            type: 'error',
          });

          return;
        }

        if (isPartial) {
          if (isDefined(buyerName)) {
            addToast(t('lookbook:selection_creation_partial_title'), {
              description: t('lookbook:selection_creation_partial_description', { buyer: buyerName, errorCount }),
              type: 'info',
            });

            return;
          }
          addToast(t('lookbook:selection_creation_partial_title'), {
            description: t('lookbook:selection_creation_partial_buyer_description', { errorCount }),
            type: 'info',
          });

          return;
        }
        if (isDefined(buyerName)) {
          addToast(t('lookbook:selection_creation_success_title'), {
            description: t('lookbook:selection_creation_success_description', { buyer: buyerName }),
            type: 'success',
          });

          return;
        }
        addToast(t('lookbook:selection_creation_success_title'), { type: 'success' });
      });
    },
  });
};
